/**
 * Shared lock state helpers for translation rows.
 */

export function normalizeLockState(request) {
  if (!request) {
    return {
      isLocked: false,
      lockedByUserId: null,
      lockedAt: null,
    };
  }
  const isLocked = Boolean(request.isLocked);
  if (!isLocked) {
    return {
      isLocked: false,
      lockedByUserId: null,
      lockedAt: null,
    };
  }
  return {
    isLocked: true,
    lockedByUserId: request.lockedByUserId || null,
    lockedAt: request.lockedAt || null,
  };
}

export function normalizeUserId(userId) {
  return (userId || '').trim().toLowerCase();
}

export function formatLockOwnerLabel(lock) {
  return lock?.lockedByUserId || 'another user';
}

/** @returns {'unlocked' | 'mine' | 'other'} */
export function getLockVisualState(lock, currentUserId) {
  if (!lock?.isLocked) return 'unlocked';
  const owner = normalizeUserId(lock.lockedByUserId);
  const current = normalizeUserId(currentUserId);
  if (owner && owner === current) return 'mine';
  return 'other';
}

export function isLockedByOther(lock, currentUserId) {
  return getLockVisualState(lock, currentUserId) === 'other';
}

export function formatLockTimestamp(iso) {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    const datePart = date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timePart = date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `Locked ${datePart} at ${timePart}`;
  } catch {
    return '';
  }
}

export function applyLockToRequest(request, lock) {
  return {
    ...request,
    isLocked: lock.isLocked,
    lockedByUserId: lock.lockedByUserId,
    lockedAt: lock.lockedAt,
  };
}

export function getLockTooltipContent(visualState, lock) {
  const lockedAtFormatted = formatLockTimestamp(lock?.lockedAt);

  if (visualState === 'unlocked') {
    return {
      title: 'Unlocked',
      description: 'Anyone can edit this translation. Click to lock it.',
      meta: '',
      accent: 'unlocked',
    };
  }
  if (visualState === 'mine') {
    return {
      title: 'Locked by You',
      description: 'Only you can unlock and edit this translation.',
      meta: lockedAtFormatted,
      accent: 'mine',
    };
  }
  const ownerLabel = formatLockOwnerLabel(lock);
  return {
    title: `Locked by ${ownerLabel}`,
    description: `Only ${ownerLabel} can unlock and edit this translation.`,
    meta: lockedAtFormatted,
    accent: 'other',
  };
}

export function getLockAriaLabel(visualState, lock) {
  if (visualState === 'unlocked') return 'Lock translation';
  if (visualState === 'mine') return 'Unlock translation';
  return `Translation locked by ${formatLockOwnerLabel(lock)}`;
}

export function formatBulkLockMessage({ locked, skippedOther, failed = 0 }) {
  let message;
  if (locked && skippedOther) {
    message = `${locked} translations locked. ${skippedOther} were already locked by other users.`;
  } else if (locked) {
    message = `${locked} translation${locked !== 1 ? 's' : ''} locked.`;
  } else if (skippedOther) {
    message = `0 translations locked. ${skippedOther} were already locked by other users.`;
  } else {
    message = 'No translations to lock.';
  }
  if (failed) {
    message += ` ${failed} failed.`;
  }
  return message;
}

export function formatBulkApproveMessage({ approved, skippedLocked, failed = 0 }) {
  let message;
  if (approved && skippedLocked) {
    message = `${approved} translation${approved !== 1 ? 's' : ''} approved. ${skippedLocked} skipped (locked by other users).`;
  } else if (approved) {
    message = `${approved} translation${approved !== 1 ? 's' : ''} approved.`;
  } else if (skippedLocked) {
    message = `0 translations approved. ${skippedLocked} skipped (locked by other users).`;
  } else {
    message = 'No translations to approve.';
  }
  if (failed) {
    message += ` ${failed} failed.`;
  }
  return message;
}

export function formatBulkUnlockMessage({ unlocked, otherLocked, failed = 0 }) {
  let message;
  if (unlocked && otherLocked) {
    message = `${unlocked} translations unlocked. ${otherLocked} locks owned by other users were unchanged.`;
  } else if (unlocked) {
    message = `${unlocked} translation${unlocked !== 1 ? 's' : ''} unlocked.`;
  } else if (otherLocked) {
    message = `0 translations unlocked. ${otherLocked} locks owned by other users were unchanged.`;
  } else {
    message = 'No translations to unlock.';
  }
  if (failed) {
    message += ` ${failed} failed.`;
  }
  return message;
}
